import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@app-evolution-cms/element-ui';

import { useAuth } from '../../providers/auth';
import { Dialog, DialogContent } from '../../ui/components/dialog';
import { formatMillisecondsToTime } from '../../utils/date-format';
import { IDLE_COUNTDOWN_TIME, IDLE_TIME } from './constants';
import { Counter } from './counter';

const THROTTLE = 500;

export function InactivityDetector() {
  const { logout } = useAuth();
  const intervalId = useRef(0);
  const [inactive, setInactive] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const onPrompt = useCallback(() => {
    !inactive && setInactive((value) => !value);
  }, [inactive]);

  const counterValues = useMemo(
    () => ({
      value: formatMillisecondsToTime(remainingTime || IDLE_COUNTDOWN_TIME),
      valuePercentage: Math.ceil(
        ((remainingTime || IDLE_COUNTDOWN_TIME) * 100) / IDLE_COUNTDOWN_TIME,
      ),
    }),
    [remainingTime],
  );

  const onActive = () => {
    message('keepSession');
    onMessage();
  };

  const onMessage = () => {
    setInactive(false);
    reset();
  };

  const { getRemainingTime, activate, message, reset } = useIdleTimer({
    crossTab: true,
    onActive,
    onMessage,
    onPrompt,
    promptBeforeIdle: IDLE_COUNTDOWN_TIME,
    syncTimers: 200,
    throttle: THROTTLE,
    timeout: IDLE_TIME,
  });

  useEffect(() => {
    if (inactive) {
      intervalId.current = window.setInterval(() => {
        const remaining = getRemainingTime();

        if (!remaining) {
          logout();
          clearInterval(intervalId.current);
          activate();
        }

        setRemainingTime(remaining);
      }, THROTTLE);
    }
  }, [inactive, activate, getRemainingTime, logout]);

  const onContinue = () => {
    activate();
  };

  return (
    <Dialog open={inactive}>
      <DialogContent className="grid justify-center gap-6" withCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Tu sesión se cerrará automáticamente por inactividad en</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <Counter valuePercentage={counterValues.valuePercentage} value={counterValues.value} />
        <DialogFooter className="mt-2 flex gap-4">
          <Button className="w-full" size="lg" variant="destructive" onClick={logout}>
            Cerrar sesión
          </Button>
          <Button className="w-full" size="lg" onClick={onContinue}>
            Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
